// src/utils/estimateSend.js — doced-8f3a61c2 (Document editor, Stage 5f: Review & send)
//
// The rules behind /estimates/:ref/send. Pure: no Firebase.
//
// THE SEND IS TODAY'S SEND. The pop-up's "Save & Send" does two things: a browser save that marks the estimate
// Sent (status, sentAt, the follow-up reminder clock, a fresh expiry if it had none or it had passed — the
// Seam 2 rules), then the sendQuoteEmail callable. Review & send does the same two things. The page always
// saves before it opens Review & send, so the stored estimate already holds everything a plain save writes;
// what the pop-up's Save & Send writes ON TOP of a plain save is exactly the "send stamp" below — computed by
// running the shared builder (estimateSave.buildEstimateWrite, the pop-up's own save, lifted and tested in
// 960 states) twice on the stored estimate, with and without Send, and keeping the difference.
// estimateSend.test.js proves the stamp = the lifted pop-up's Save & Send minus its Save, on every state.
// The email then goes through the same sendQuoteEmail with Review & send's optional cc / subject / message /
// attachPdf (Stage 5a; without them the email is byte-identical to today's). Every pipeline record follows from
// those two steps exactly as before: Sent, the sent stage, the activity row, follow-ups, the portal link, views.

import { buildEstimateWrite } from './estimateSave';
import { initialQuoteData } from './estimateEditor';
import { normalizeStatus, QUOTE_STATUSES, isDepositAmountNeeded, DEPOSIT_AMOUNT_NEEDED_MESSAGE } from './quoteSchema';

// ── who can be sent from the page ───────────────────────────────────────────
// Sending marks the estimate Sent, so it is never offered where that would undo what the customer did or
// what already happened: approved, awaiting a deposit, declined, or turned into a job. (The pop-up's Save &
// Send does not check this — founder: tracked separately, not changed here.)
const NOT_SENDABLE = [QUOTE_STATUSES.APPROVED, QUOTE_STATUSES.AWAITING_DEPOSIT, QUOTE_STATUSES.DECLINED];

/** null when the estimate can be sent from the page, else the reason in plain words. */
export function sendBlockedReason(quote, docWord = 'estimate') {
    if (!quote) return `This ${docWord} couldn’t be loaded.`;
    if (quote.convertedToJobId) return `This ${docWord} has already been turned into a job, so it can’t be sent again from here.`;
    const s = normalizeStatus(quote.status);
    if (s === QUOTE_STATUSES.APPROVED) return `This ${docWord} is approved. Sending it would mark it Sent again, so it can’t be sent from here.`;
    if (s === QUOTE_STATUSES.AWAITING_DEPOSIT) return `This ${docWord} is approved and waiting for its deposit, so it can’t be sent again from here.`;
    if (s === QUOTE_STATUSES.DECLINED) return `This ${docWord} was declined. Re-quote it to send the customer a new one.`;
    if (isDepositAmountNeeded(quote)) return DEPOSIT_AMOUNT_NEEDED_MESSAGE; // depdraft-3c8e5a14 — the server refuses it too
    return null;
}
export const canSendFromPage = (quote) => sendBlockedReason(quote) === null;
/** For the editor's button: the status the editor is about to save (it saves before Review & send). */
export const statusAllowsSend = (status) => !NOT_SENDABLE.includes(normalizeStatus(status));

/** The address the email goes to: always the estimate's own (the server reads only this). */
export const recipientOf = (quote) => (quote && ((quote.customerDetails && quote.customerDetails.email) || quote.customerEmail)) || '';

// ── CC — the same rules sendQuoteEmail applies (functions/utils/quoteSendOptions.js) ──
export const MAX_CC = 5;
export const MAX_SUBJECT = 200;
export const MAX_MESSAGE = 5000;
const EMAIL_RE = /^[^\s@<>"',;]+@[^\s@<>"',;]+\.[^\s@<>"',;]+$/;

/**
 * The CC box, read. Addresses may be separated by commas, semicolons, spaces or new lines.
 * → { list: string[] (as typed, blanks removed), problem: string|null }
 */
export function parseCcInput(text) {
    const list = String(text || '').split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    const bad = list.find((e) => e.length > 254 || !EMAIL_RE.test(e));
    if (bad) return { list, problem: `${bad} isn’t a valid email address.` };
    if (list.length > MAX_CC) return { list, problem: `You can CC at most ${MAX_CC} addresses.` };
    return { list, problem: null };
}

/** The subject / message problems, in the server's words. */
export function subjectProblem(subject) {
    const s = String(subject || '').trim();
    if (!s) return 'Add a subject.';
    if (/[\u0000-\u001f\u007f]/.test(s)) return 'The subject must be a single line.'; // eslint-disable-line no-control-regex
    if (s.length > MAX_SUBJECT) return `The subject can be at most ${MAX_SUBJECT} characters.`;
    return null;
}
export function messageProblem(message) {
    const m = String(message || '').replace(/\r\n?/g, '\n');
    if (!m.trim()) return 'Add a message.';
    if (m.length > MAX_MESSAGE) return `The message can be at most ${MAX_MESSAGE.toLocaleString('en-US')} characters.`;
    return null;
}

/** The sendQuoteEmail request: only the keys it accepts; CC only when there is some. */
export function sendRequest({ quoteId, cc, subject, message, attachPdf }) {
    return {
        quoteId,
        ...(cc && cc.length ? { cc } : {}),
        subject: String(subject).trim(),
        message: String(message).replace(/\r\n?/g, '\n'),
        attachPdf: attachPdf === true,
    };
}
/** The previewQuoteEmail request (no attachPdf; blank subject / message are left out, not refused). */
export function previewRequest({ quoteId, cc, subject, message }) {
    return {
        quoteId,
        ...(cc && cc.length ? { cc } : {}),
        ...(String(subject || '').trim() && !subjectProblem(subject) ? { subject: String(subject).trim() } : {}),
        ...(!messageProblem(message) ? { message: String(message).replace(/\r\n?/g, '\n') } : {}),
    };
}

// ── the send stamp ──────────────────────────────────────────────────────────
// The only keys a Save & Send may add on top of a Save. Anything else differing is a bug: refuse.
export const SEND_STAMP_KEYS = Object.freeze(['status', 'sentAt', 'followUpRemindersEnabled', 'followUpRemindersSent', 'expiresAt']);

/**
 * What Save & Send writes on top of a plain Save, for the STORED estimate (merge-written).
 * deps = { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds, newLineId, today, defaultExpiry,
 *          companySettings, newLine, generateItemId }
 */
export function buildSendStamp(stored, deps) {
    const { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds, newLineId, today, defaultExpiry, companySettings, newLine, generateItemId } = deps;
    const quoteData = initialQuoteData(stored, { today, defaultExpiry, newLine, generateItemId, companySettings });
    // Both runs take line ids from ONE list (filled from newLineId as needed), so a line that lacks an id gets
    // the same one in each run and the lines never differ between them.
    const pool = [];
    const sameIds = () => { let i = 0; return () => { if (i === pool.length) pool.push(newLineId()); return pool[i++]; }; };
    const run = (sendEmail) => buildEstimateWrite({
        quoteData, editingQuote: stored, sendEmail,
        totals: { subtotal: stored.subtotal, taxAmount: stored.taxAmount, total: stored.total },
        processedLineItems: quoteData.lineItems,
        deposit: { locked: true }, // no deposit fields in either run (equal either way)
        today, defaultExpiry,
    }, { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds, newLineId: sameIds() });
    const send = run(true);
    const plain = run(false);
    const same = (a, b) => JSON.stringify(a) === JSON.stringify(b) && (a instanceof Date) === (b instanceof Date)
        && (!(a instanceof Date) || a.getTime() === b.getTime());
    const stamp = {};
    for (const k of new Set([...Object.keys(send), ...Object.keys(plain)])) {
        if (k === 'updatedAt' || k === 'lastActivityAt') continue; // server stamps, added below
        if ((k in send) === (k in plain) && same(send[k], plain[k])) continue;
        if (!SEND_STAMP_KEYS.includes(k) || !(k in send)) throw new Error(`send stamp: unexpected difference in "${k}"`);
        stamp[k] = send[k];
    }
    // A save always stamps these two (the pop-up's Save & Send does too).
    stamp.updatedAt = send.updatedAt;
    stamp.lastActivityAt = send.lastActivityAt;
    return stamp;
}

/** "Replies go to you (x)" — or to the salesperson, by name, when that is who the server chose. */
export function replyLine({ replyToEmail, replyToName }, currentUserEmail) {
    if (!replyToEmail) return '';
    const mine = currentUserEmail && replyToEmail.toLowerCase() === String(currentUserEmail).toLowerCase();
    return mine ? `Replies go to you (${replyToEmail}), not to Spatchy.` : `Replies go to ${replyToName ? `${replyToName} (${replyToEmail})` : replyToEmail}, not to Spatchy.`;
}
